import apiClient from "../../../services/apiClient";
import { API_ENDPOINTS } from "../../../config/apiEndpoints";
import { getUserData } from "../../../services/apiService";

function orgStaffPayload(extra = {}) {
  const user = getUserData() || {};
  return {
    OrganizationId: user.OrganizationId || user.organizationId || "",
    StaffId: user.StaffId || user.staffId || "",
    ...extra,
  };
}

function getDataset(response) {
  if (!response || typeof response !== "object") return null;
  return response.data ?? response.Data ?? null;
}

function firstTable(ds) {
  if (!ds || typeof ds !== "object") return [];
  if (Array.isArray(ds.Data)) return ds.Data;
  if (Array.isArray(ds.data)) return ds.data;
  const keys = Object.keys(ds);
  for (const key of keys) {
    if (Array.isArray(ds[key])) return ds[key];
  }
  return [];
}

async function postEpsf(url, body) {
  const response = await apiClient.post(url, orgStaffPayload(body));
  const success = Number(response?.success ?? response?.Success ?? 0) === 1;
  const message = response?.message || response?.Message || "";
  const ds = getDataset(response);
  return { success, message, ds, rows: firstTable(ds), response };
}

export async function fetchCustodySummary(studyId, siteId) {
  const result = await postEpsf(API_ENDPOINTS.EPSF.GET_CUSTODY_SUMMARY, {
    StudyId: studyId,
    SiteId: siteId,
  });
  const row = result.rows[0] || {};
  return {
    ...result,
    custody: {
      received: Number(row.Received ?? row.received ?? 0),
      onHand: Number(row.OnHand ?? row.onHand ?? 0),
      dispensed: Number(row.Dispensed ?? row.dispensed ?? 0),
      returned: Number(row.Returned ?? row.returned ?? 0),
      destroyed: Number(row.Destroyed ?? row.destroyed ?? 0),
    },
  };
}

export async function fetchImpProducts(studyId, siteId, isActive = null) {
  // Catalogue is organisation-global; study/site args kept for call-site compatibility only.
  return postEpsf(API_ENDPOINTS.EPSF.LIST_PRODUCTS, {
    StudyId: studyId || null,
    SiteId: siteId || null,
    IsActive: isActive,
  });
}

export async function saveImpProduct(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SAVE_PRODUCT, payload);
}

export async function fetchEpsfStudyConfig(studyId) {
  return postEpsf(API_ENDPOINTS.EPSF.GET_STUDY_CONFIG, {
    StudyId: studyId,
  });
}

export async function saveEpsfStudyConfig(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SAVE_STUDY_CONFIG, payload);
}

export async function fetchImpTxns(studyId, siteId, txnType = "Receipt") {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_TXNS, {
    StudyId: studyId,
    SiteId: siteId,
    TxnType: txnType || null,
  });
}

export async function postImpReceipt(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_RECEIPT, payload);
}

export async function fetchReceiveModes(studyId, siteId) {
  const result = await postEpsf(API_ENDPOINTS.EPSF.GET_RECEIVE_MODES, {
    StudyId: studyId,
    SiteId: siteId,
  });
  const row = result.rows[0] || {};
  return {
    ...result,
    modes: {
      hasBatchQuantity: Number(row.HasBatchQuantity ?? row.hasBatchQuantity ?? 0) === 1,
      hasIndividualKit: Number(row.HasIndividualKit ?? row.hasIndividualKit ?? 0) === 1,
      defaultMode:
        row.DefaultMode ||
        row.defaultMode ||
        (Number(row.HasIndividualKit ?? 0) === 1 ? "IndividualKit" : "BatchQuantity"),
    },
  };
}

export async function validateKitShipment(
  studyId,
  siteId,
  kitNumbers,
  storageUnitId = null,
  kitLines = null,
) {
  const result = await postEpsf(API_ENDPOINTS.EPSF.VALIDATE_KIT_SHIPMENT, {
    StudyId: studyId,
    SiteId: siteId,
    KitNumbers: Array.isArray(kitNumbers)
      ? JSON.stringify(kitNumbers)
      : kitNumbers,
    KitLinesJson: Array.isArray(kitLines) ? JSON.stringify(kitLines) : kitLines,
    StorageUnitId: storageUnitId || null,
  });
  const ds = result.ds || {};
  const summaryRow =
    (Array.isArray(ds.Data) && ds.Data[0]) ||
    (Array.isArray(ds.data) && ds.data[0]) ||
    result.rows[0] ||
    {};
  const kitRows =
    (Array.isArray(ds.Txns) && ds.Txns) ||
    (Array.isArray(ds.txns) && ds.txns) ||
    [];
  return {
    ...result,
    summary: {
      kitCount: Number(summaryRow.KitCount ?? summaryRow.kitCount ?? 0),
      expectedCount: Number(summaryRow.ExpectedCount ?? summaryRow.expectedCount ?? 0),
      unresolvedCount: Number(
        summaryRow.UnresolvedCount ?? summaryRow.unresolvedCount ?? 0,
      ),
      alreadyReceivedCount: Number(
        summaryRow.AlreadyReceivedCount ?? summaryRow.alreadyReceivedCount ?? 0,
      ),
      duplicateInRequestCount: Number(
        summaryRow.DuplicateInRequestCount ??
          summaryRow.duplicateInRequestCount ??
          0,
      ),
      batchExpiryMismatchCount: Number(
        summaryRow.BatchExpiryMismatchCount ??
          summaryRow.batchExpiryMismatchCount ??
          0,
      ),
      storageVerified:
        Number(summaryRow.StorageVerified ?? summaryRow.storageVerified ?? 0) ===
        1,
      storageNeedsOverride:
        Number(
          summaryRow.StorageNeedsOverride ??
            summaryRow.storageNeedsOverride ??
            0,
        ) === 1,
    },
    kitRows,
  };
}

export async function releaseImpReceipt(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.RELEASE_RECEIPT, payload);
}

export async function cancelReturnPending(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CANCEL_RETURN_PENDING, payload);
}

export async function fetchReturnWeeklyTrend(studyId, siteId) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_RETURN_WEEKLY_TREND, {
    StudyId: studyId,
    SiteId: siteId,
  });
}

export async function fetchHeldReceipts(studyId, siteId) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_HELD_RECEIPTS, {
    StudyId: studyId,
    SiteId: siteId,
  });
}

export async function fetchReceiptDetail(studyId, siteId, impTxnId) {
  return postEpsf(API_ENDPOINTS.EPSF.GET_RECEIPT_DETAIL, {
    StudyId: studyId,
    SiteId: siteId,
    ImpTxnId: impTxnId,
  });
}

export async function uploadReceiptEvidence({
  studyId,
  siteId,
  impTxnId,
  evidenceType,
  notes,
  file,
  evidenceScope = null,
}) {
  const user = getUserData() || {};
  const form = new FormData();
  form.append("OrganizationId", user.OrganizationId || user.organizationId || "");
  form.append("StaffId", user.StaffId || user.staffId || "");
  form.append("StudyId", studyId);
  form.append("SiteId", siteId);
  form.append("ImpTxnId", impTxnId);
  form.append("EvidenceType", evidenceType || "Other");
  form.append("Notes", notes || "");
  if (evidenceScope) {
    form.append("EvidenceScope", evidenceScope);
  }
  form.append("file", file);
  const response = await apiClient.post(
    API_ENDPOINTS.EPSF.UPLOAD_RECEIPT_EVIDENCE,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  const success = Number(response?.success ?? response?.Success ?? 0) === 1;
  return {
    success,
    message: response?.message || response?.Message || "",
    rows: firstTable(getDataset(response)),
    response,
  };
}

export async function fetchImpKits(studyId, siteId, { impBatchId, impProductId } = {}) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_KITS, {
    StudyId: studyId,
    SiteId: siteId,
    ImpBatchId: impBatchId || null,
    ImpProductId: impProductId || null,
  });
}

export async function fetchStudyParticipants(studyId, siteId = null) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_STUDY_PARTICIPANTS, {
    StudyId: studyId,
    SiteId: siteId || null,
  });
}

/** Participant Profile: Study IMP summary from ePSF dispenses (no Medication History duplication). */
export async function fetchParticipantImpSummary(participantId) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_PARTICIPANT_IMP_SUMMARY, {
    ParticipantId: participantId,
  });
}

export async function fetchParticipantImpDispenses({
  participantId,
  studyId,
  siteId,
  impProductId,
}) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_PARTICIPANT_IMP_DISPENSES, {
    ParticipantId: participantId,
    StudyId: studyId,
    SiteId: siteId,
    ImpProductId: impProductId,
  });
}

export async function fetchImpReconciles(studyId, siteId) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_RECONCILES, {
    StudyId: studyId,
    SiteId: siteId,
  });
}

export async function postImpReconcile(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_RECONCILE, payload);
}

export async function fetchStudyImpProducts(studyId, isActive = true, siteId = null) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_STUDY_IMP_PRODUCTS, {
    StudyId: studyId,
    SiteId: siteId || null,
    IsActive: isActive,
  });
}

export async function saveStudyImpProduct(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SAVE_STUDY_IMP_PRODUCT, payload);
}

export async function fetchStudyImpKitMaps(studyId, siteId = null) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_STUDY_IMP_KIT_MAPS, {
    StudyId: studyId,
    SiteId: siteId || null,
    IsActive: true,
  });
}

/** Prepared batches (kit-mapped) that have not been received yet — for Add Receipt search. */
export async function fetchReceivableBatches(studyId, siteId = null) {
  const result = await postEpsf(API_ENDPOINTS.EPSF.LIST_RECEIVABLE_BATCHES, {
    StudyId: studyId,
    SiteId: siteId || null,
  });
  const ds = result.ds || {};
  const rows = Array.isArray(ds.Data)
    ? ds.Data
    : Array.isArray(ds.data)
      ? ds.data
      : result.rows || [];
  return { ...result, rows };
}

export async function saveStudyImpKitMaps(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SAVE_STUDY_IMP_KIT_MAPS, payload);
}

export async function postEmergencyUnblind(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_EMERGENCY_UNBLIND, payload);
}

export async function postImpAdjustment(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_ADJUSTMENT, payload);
}

export async function fetchImpTransfers(studyId, siteId = null) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_TRANSFERS, {
    StudyId: studyId,
    SiteId: siteId || null,
  });
}

export async function createImpTransfer(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CREATE_TRANSFER, payload);
}

export async function despatchImpTransfer(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.DESPATCH_TRANSFER, payload);
}

export async function confirmImpTransferArrival(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CONFIRM_TRANSFER_ARRIVAL, payload);
}

export async function fetchImpBalances(studyId, siteId) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_BALANCES, {
    StudyId: studyId,
    SiteId: siteId,
  });
}

export async function postImpDispense(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_DISPENSE, payload);
}

export async function createDispenseRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CREATE_DISPENSE_REQUEST, payload);
}

export async function confirmDispenseRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CONFIRM_DISPENSE_REQUEST, payload);
}

export async function createDestroyRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CREATE_DESTROY_REQUEST, payload);
}

export async function confirmDestroyRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CONFIRM_DESTROY_REQUEST, payload);
}

export async function createReturnRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CREATE_RETURN_REQUEST, payload);
}

export async function confirmReturnRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CONFIRM_RETURN_REQUEST, payload);
}

export async function createStockReturnRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CREATE_STOCK_RETURN_REQUEST, payload);
}

export async function confirmStockReturnRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CONFIRM_STOCK_RETURN_REQUEST, payload);
}

export async function createReconcileRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CREATE_RECONCILE_REQUEST, payload);
}

export async function confirmReconcileRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CONFIRM_RECONCILE_REQUEST, payload);
}

export async function postImpReturn(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_RETURN, payload);
}

export async function postImpStockReturn(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_STOCK_RETURN, payload);
}

export async function fetchReturnableDispenses(studyId, siteId) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_RETURNABLE_DISPENSES, {
    StudyId: studyId,
    SiteId: siteId,
  });
}

export async function postImpDestroy(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_DESTROY, payload);
}

export async function fetchStorageUnits(studyId, siteId) {
  const result = await postEpsf(API_ENDPOINTS.EPSF.LIST_STORAGE_UNITS, {
    StudyId: studyId,
    SiteId: siteId,
    IsActive: true,
  });
  const ds = result.ds;
  const racks =
    (Array.isArray(ds?.Txns) && ds.Txns) ||
    (Array.isArray(ds?.txns) && ds.txns) ||
    (Array.isArray(ds?.Racks) && ds.Racks) ||
    (Array.isArray(ds?.racks) && ds.racks) ||
    [];
  return { ...result, racks };
}

export async function saveStorageUnit(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SAVE_STORAGE_UNIT, payload);
}

export async function fetchTempReadings(studyId, siteId, storageUnitId = null) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_TEMP_READINGS, {
    StudyId: studyId,
    SiteId: siteId,
    StorageUnitId: storageUnitId || null,
  });
}

export async function fetchTempExcursions(studyId, siteId) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_TEMP_EXCURSIONS, {
    StudyId: studyId,
    SiteId: siteId,
  });
}

export async function postTempReading(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_TEMP_READING, payload);
}

export async function fetchFormTriggers(studyId = null, siteId = null, isActive = true) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_FORM_TRIGGERS, {
    StudyId: studyId || null,
    SiteId: siteId || null,
    IsActive: isActive,
  });
}

export async function saveFormTrigger(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SAVE_FORM_TRIGGER, payload);
}

/** Alias of saveFormTrigger for Form Requirements configuration. */
export async function saveFormRequirement(payload) {
  return saveFormTrigger(payload);
}

export async function fetchFormOccurrences(studyId, siteId, status = null) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_FORM_OCCURRENCES, {
    StudyId: studyId,
    SiteId: siteId,
    Status: status,
  });
}

export async function fetchFormWorkItems(studyId, siteId, status = "OPEN") {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_FORM_WORK_ITEMS, {
    StudyId: studyId,
    SiteId: siteId,
    Status: status,
  });
}

export async function ensureFormOccurrence(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.ENSURE_FORM_OCCURRENCE, payload);
}

export async function createManualFormOccurrence(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CREATE_MANUAL_FORM_OCCURRENCE, payload);
}

export async function ensureRecurringFormDues(studyId, siteId) {
  return postEpsf(API_ENDPOINTS.EPSF.ENSURE_RECURRING_FORM_DUES, {
    StudyId: studyId,
    SiteId: siteId,
  });
}

export async function linkFormInstance(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.LINK_FORM_INSTANCE, payload);
}

export async function completeFormOccurrence(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.COMPLETE_FORM_OCCURRENCE, payload);
}

export async function submitFormWorkItem(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SUBMIT_FORM_WORK_ITEM, payload);
}

export async function approveFormWorkItem(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.APPROVE_FORM_WORK_ITEM, payload);
}

export async function rejectFormWorkItem(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.REJECT_FORM_WORK_ITEM, payload);
}

/** Full form lifecycle timeline (initiated → dues → fill → approve/reject → correction). */
export async function fetchFormLifecycle(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.GET_FORM_LIFECYCLE, payload);
}

/** Batch + kit stage context for a form linked to an IMP receipt/dispense/destroy txn. */
export async function fetchFormKitContext(payload) {
  const result = await postEpsf(API_ENDPOINTS.EPSF.GET_FORM_KIT_CONTEXT, payload);
  const ds = result.ds || {};
  const namedData =
    (Array.isArray(ds.Data) && ds.Data) ||
    (Array.isArray(ds.data) && ds.data) ||
    null;
  const namedKits =
    (Array.isArray(ds.Txns) && ds.Txns) ||
    (Array.isArray(ds.txns) && ds.txns) ||
    null;
  const arrayTables = Object.keys(ds)
    .filter((k) => Array.isArray(ds[k]))
    .map((k) => ds[k]);
  const summaryRows = namedData || arrayTables[0] || result.rows || [];
  const kitRows = namedKits || arrayTables[1] || [];
  const summary = summaryRows[0] || null;
  return {
    ...result,
    summary,
    kits: Array.isArray(kitRows) ? kitRows : [],
  };
}

export async function checkFormReleaseBlockers(studyId, siteId, linkedTxnId = null) {
  return postEpsf(API_ENDPOINTS.EPSF.CHECK_FORM_RELEASE_BLOCKERS, {
    StudyId: studyId,
    SiteId: siteId,
    LinkedTxnId: linkedTxnId || null,
  });
}

/** Block final IMP actions until required forms are complete/approved. */
export async function checkFormActionBlockers(
  studyId,
  siteId,
  blockingActionCode,
  linkedTxnId = null,
) {
  return postEpsf(API_ENDPOINTS.EPSF.CHECK_FORM_ACTION_BLOCKERS, {
    StudyId: studyId,
    SiteId: siteId,
    BlockingActionCode: blockingActionCode,
    LinkedTxnId: linkedTxnId || null,
  });
}

/** Local calendar period key (yyyyMMdd) for site-scoped event form dues. */
export function localFormPeriodKey(date = new Date()) {
  const d = date instanceof Date ? date : new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

export function formBlockerTitles(result) {
  const rows = Array.isArray(result?.rows) ? result.rows : [];
  return rows
    .map(
      (b) =>
        b.FormTitle ||
        b.formTitle ||
        b.Title ||
        b.title ||
        "Required form",
    )
    .filter(Boolean);
}

export function formBlockersNeedRequest(result) {
  const rows = Array.isArray(result?.rows) ? result.rows : [];
  return rows.some(
    (b) =>
      b.NeedsRequest === true ||
      b.needsRequest === true ||
      Number(b.NeedsRequest) === 1 ||
      Number(b.needsRequest) === 1,
  );
}

export function formBlockMessageForAction(result, actionLabel) {
  const titles = formBlockerTitles(result);
  if (!titles.length) return "";
  const label = actionLabel || "this action";
  if (formBlockersNeedRequest(result)) {
    return `${label} requires a request first so the form becomes due. Use Create request, complete and approve: ${titles.join("; ")}.`;
  }
  return `${label} is blocked until these form(s) are completed and approved: ${titles.join("; ")}.`;
}

export async function fetchDuties() {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_DUTIES, { ModuleScope: "ePSF", IsActive: true });
}

export async function fetchDutyAssignments(filters = {}) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_DUTY_ASSIGNMENTS, filters);
}

export async function assignDuty(payload) {
  const user = getUserData() || {};
  return postEpsf(API_ENDPOINTS.EPSF.ASSIGN_DUTY, {
    ...payload,
    ActorStaffId: user.StaffId || user.staffId || "",
  });
}

export async function revokeDutyAssignment(dutyAssignmentId) {
  return postEpsf(API_ENDPOINTS.EPSF.REVOKE_DUTY_ASSIGNMENT, {
    DutyAssignmentId: dutyAssignmentId,
  });
}

export async function postImpRecall(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.POST_RECALL, payload);
}

export async function fetchInspectionPack(studyId, siteId) {
  const result = await postEpsf(API_ENDPOINTS.EPSF.GET_INSPECTION_PACK, {
    StudyId: studyId,
    SiteId: siteId,
  });
  const ds = result.ds || {};
  const table = (name) => {
    if (Array.isArray(ds[name])) return ds[name];
    if (ds[name]?.Rows && Array.isArray(ds[name].Rows)) return ds[name].Rows;
    return [];
  };
  return {
    ...result,
    custody: (result.rows && result.rows[0]) || table("Data")[0] || {},
    txns: table("Txns"),
    tempReadings: table("TempReadings"),
    formOccurrences: table("FormOccurrences"),
  };
}

export async function fetchStudiesForEpsf() {
  const user = getUserData() || {};
  const response = await apiClient.post(API_ENDPOINTS.STUDY.GET_ALL_STUDIES, {
    OrganizationId: user.OrganizationId || user.organizationId || "",
    StaffIdRef: user.StaffId || user.staffId || "",
    CreatedBy: user.StaffId || user.staffId || "",
    Action: "GetStudyOrscheduledlist",
    AccessCode: "",
    Password: "",
  });
  const success = Number(response?.success ?? response?.Success ?? 0) === 1;
  const rows = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.Data)
      ? response.Data
      : [];
  const options = rows
    .map((row) => {
      const studyIdRef = String(
        row?.StudyIdRef ?? row?.studyIdRef ?? "",
      ).trim();
      const scheduleId = String(
        row?.StudyScheduleId ?? row?.studyScheduleId ?? "",
      ).trim();
      const id = String(
        row?.StudyId ??
          row?.StudyIdRef ??
          row?.studyId ??
          row?.StudyScheduleId ??
          "",
      ).trim();
      /* Prefer StudyIdRef — matches StaffExternalEntityAccess / trial StudyId. */
      const value = studyIdRef || id || scheduleId;
      const num = row?.StudyNumber || row?.StudyCode || "";
      const name = row?.StudyName || "";
      const label = num && name ? `${num} — ${name}` : name || num || String(value);
      return {
        value,
        label,
        studyIdRef: studyIdRef || value,
        studyScheduleId: scheduleId || "",
      };
    })
    .filter((o) => o.value);
  return { success, options };
}

export async function fetchSitesForEpsf(studyId = null) {
  if (studyId) {
    const result = await postEpsf(API_ENDPOINTS.EPSF.LIST_STUDY_SITES, {
      StudyId: studyId,
    });
    const options = (result.rows || [])
      .map((row) => {
        const id = row?.SiteId ?? row?.SiteID ?? row?.siteId ?? "";
        const name = row?.SiteName || row?.Name || "";
        const code = row?.SiteAbbr || row?.SiteCode || row?.Code || "";
        const label =
          code && name ? `${code} — ${name}` : name || code || String(id);
        return { value: String(id), label };
      })
      .filter((o) => o.value);
    return { success: result.success, options, message: result.message };
  }

  const user = getUserData() || {};
  const response = await apiClient.post(API_ENDPOINTS.SITE.SITE_ACTIVITY, {
    SiteId: "",
    SiteName: "",
    SiteAbbr: "",
    Location: "",
    ContactName: "",
    ContactPhone: "",
    ContactEmail: "",
    Address: "",
    Postcode: "",
    OdsCode: "",
    NhsNumber: "",
    StaffId: "",
    StudyId: "",
    OrganizationId: user.OrganizationId || user.organizationId || "",
    CreatedBy: user.StaffId || user.staffId || "",
    ActionType: "GetAllSites",
  });
  const success = Number(response?.success ?? response?.Success ?? 0) === 1;
  const raw =
    response?.data?.SuccessStory ||
    response?.data?.successStory ||
    response?.data ||
    response?.Data ||
    [];
  const rows = Array.isArray(raw) ? raw : [];
  const options = rows
    .map((row) => {
      const id = row?.SiteId ?? row?.SiteID ?? row?.siteId ?? "";
      const name = row?.SiteName || row?.Name || "";
      const code = row?.SiteAbbr || row?.SiteCode || row?.Code || "";
      const label = code && name ? `${code} — ${name}` : name || code || String(id);
      return { value: String(id), label };
    })
    .filter((o) => o.value);
  return { success, options };
}

const CONTEXT_KEY = "aq_epsf_context";

export function loadEpsfContext() {
  try {
    const raw = sessionStorage.getItem(CONTEXT_KEY);
    if (!raw) return { studyId: "", siteId: "" };
    const parsed = JSON.parse(raw);
    return {
      studyId: String(parsed.studyId || ""),
      siteId: String(parsed.siteId || ""),
    };
  } catch {
    return { studyId: "", siteId: "" };
  }
}

export function saveEpsfContext({ studyId, siteId }) {
  sessionStorage.setItem(
    CONTEXT_KEY,
    JSON.stringify({
      studyId: String(studyId || ""),
      siteId: String(siteId || ""),
    }),
  );
}

function idUpper(v) {
  return v == null || v === "" ? "" : String(v).toUpperCase();
}

function normId(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

function splitIdList(raw) {
  return String(raw ?? "")
    .split(/[,;|]/)
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
}

/** Staff has access to site via mapped sites, site refs, or primary site. */
function staffHasSiteAccess(staff, siteId) {
  const needle = normId(siteId);
  if (!needle || !staff) return false;

  const roleName = String(staff.RoleName || staff.Roles || staff.roleName || "")
    .trim()
    .toLowerCase();
  if (roleName === "system administrator") return true;

  const ids = new Set([
    ...splitIdList(staff.MappedSiteIds),
    ...splitIdList(staff.mappedSiteIds),
    ...splitIdList(staff.SiteIdRef),
    ...splitIdList(staff.SiteIds),
    ...splitIdList(staff.PrimarySiteId),
    ...splitIdList(staff.primarySiteId),
    ...splitIdList(staff.SiteId),
  ]);
  return ids.has(needle);
}

function pickStaffIdFromRow(row) {
  if (!row || typeof row !== "object") return "";
  return normId(
    row.StaffId ||
      row.staffId ||
      row.UserId ||
      row.userId ||
      row.StaffID ||
      row.StaffIDRef ||
      row.staffIDRef ||
      row.UserIdRef ||
      row.userIdRef ||
      "",
  );
}

function isAssignedUserRow(u) {
  if (u == null || typeof u !== "object") return false;
  const v = u.IsAssigned ?? u.isAssigned;
  if (v === undefined || v === null || v === "") return true;
  if (v === false || v === 0 || v === "0") return false;
  return (
    v === true ||
    v === 1 ||
    v === "1" ||
    String(v).toLowerCase() === "true" ||
    String(v).toLowerCase() === "yes"
  );
}

function collectStudyUserRows(node, into, depth = 0) {
  if (!node || depth > 8) return;
  if (Array.isArray(node)) {
    for (const row of node) {
      if (!row || typeof row !== "object") continue;
      const id = pickStaffIdFromRow(row);
      // GetStudyInfo StudyUsers is often { StaffId } only — still a user row.
      if (id) {
        into.push(row);
        continue;
      }
      collectStudyUserRows(row, into, depth + 1);
    }
    return;
  }
  if (typeof node !== "object") return;

  const preferredKeys = [
    "StudyUsers",
    "studyUsers",
    "UserList",
    "userList",
    "AssignedUsers",
    "assignedUsers",
    "StudyStaffList",
    "studyStaffList",
  ];
  for (const key of preferredKeys) {
    if (node[key] != null) collectStudyUserRows(node[key], into, depth + 1);
  }

  for (const [key, value] of Object.entries(node)) {
    if (preferredKeys.includes(key)) continue;
    if (/^table\d*$/i.test(key) || /studyuser|userlist|assigneduser|stafflist/i.test(key)) {
      collectStudyUserRows(value, into, depth + 1);
    }
  }

  const info =
    node.StudyInfo ||
    node.studyInfo ||
    (Array.isArray(node.StudyInfoList) ? node.StudyInfoList[0] : null) ||
    (Array.isArray(node.StudyInfos) ? node.StudyInfos[0] : null);
  if (info && info !== node) collectStudyUserRows(info, into, depth + 1);
}

/**
 * Roles for ePSF form assignment on a study + site:
 * distinct roles of staff assigned to the study who also have access to the site,
 * always including System Administrator.
 */
export async function fetchRolesForStudySite(studyId, siteId) {
  const user = getUserData() || {};
  const orgId = user.OrganizationId || user.organizationId || "";
  const staffId = user.StaffId || user.staffId || "";

  const { fetchRoleLookup, fetchStaffLookup } = await import(
    "../../../services/lookupService"
  );

  const allRoles = await fetchRoleLookup(user).catch(() => []);
  const sysAdmin =
    (allRoles || []).find((r) =>
      /^system\s+administrator$/i.test(String(r?.roleName || "").trim()),
    ) || null;

  const finish = (roles) => {
    const map = new Map();
    (roles || []).forEach((r) => {
      const roleId = String(r?.roleId || "").trim();
      const roleName = String(r?.roleName || "").trim();
      if (!roleId || !roleName) return;
      map.set(roleId.toLowerCase(), { roleId, roleName });
    });
    if (sysAdmin?.roleId) {
      map.set(String(sysAdmin.roleId).toLowerCase(), {
        roleId: String(sysAdmin.roleId),
        roleName: String(sysAdmin.roleName),
      });
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.roleName).localeCompare(String(b.roleName)),
    );
  };

  if (!studyId || !siteId || !orgId || !staffId) {
    return finish([]);
  }

  const [studyRes, staffList] = await Promise.all([
    apiClient
      .post(API_ENDPOINTS.STUDY.STUDY_ACTIVITY, {
        OrganizationId: idUpper(orgId),
        CreatedBy: idUpper(staffId),
        StaffIdRef: idUpper(staffId),
        StudyId: idUpper(studyId),
        Action: "GetStudyInfo",
      })
      .catch(() => null),
    fetchStaffLookup(user).catch(() => []),
  ]);

  const userRows = [];
  if (studyRes && typeof studyRes === "object") {
    collectStudyUserRows(studyRes, userRows);
    collectStudyUserRows(studyRes.data || studyRes.Data, userRows);
    collectStudyUserRows(
      studyRes.data?.data || studyRes.Data?.Data,
      userRows,
    );
  }

  const assignedIds = new Set();
  const roleHintByStaff = new Map();

  const pushUserRow = (row, respectAssignFlag) => {
    if (!row || typeof row !== "object") return;
    if (respectAssignFlag && !isAssignedUserRow(row)) return;
    const sid = pickStaffIdFromRow(row);
    if (!sid) return;
    assignedIds.add(sid);
    const roleId = String(row.RoleId || row.roleId || row.RoleIdRef || "").trim();
    const roleName = String(
      row.RoleName || row.Roles || row.roleName || "",
    ).trim();
    if (roleId || roleName) {
      roleHintByStaff.set(sid, { roleId, roleName });
    }
  };

  for (const row of userRows) pushUserRow(row, true);
  if (!assignedIds.size) {
    for (const row of userRows) pushUserRow(row, false);
  }

  // PI often on study info but omitted from StudyUsers
  const infoBags = [
    studyRes,
    studyRes?.data,
    studyRes?.Data,
    studyRes?.data?.data,
    studyRes?.Data?.Data,
  ];
  for (const bag of infoBags) {
    if (!bag || typeof bag !== "object") continue;
    const info =
      bag.StudyInfo ||
      bag.studyInfo ||
      (Array.isArray(bag.StudyInfoList) ? bag.StudyInfoList[0] : null) ||
      bag;
    const piId = pickStaffIdFromRow({
      StaffId:
        info?.PrincipalInvestigator ||
        info?.principalInvestigator ||
        info?.PIStaffId ||
        info?.piStaffId ||
        info?.PI ||
        info?.pi,
    });
    if (piId) assignedIds.add(piId);
  }

  const roleMap = new Map();
  const addRole = (roleIdRaw, roleNameRaw) => {
    let roleId = String(roleIdRaw || "").trim();
    let roleName = String(roleNameRaw || "").trim();
    if (!roleId && roleName) {
      const byName = (allRoles || []).find(
        (r) =>
          String(r.roleName || "")
            .trim()
            .toLowerCase() === roleName.toLowerCase(),
      );
      if (byName) {
        roleId = String(byName.roleId || "");
        roleName = String(byName.roleName || roleName);
      }
    }
    if (!roleId && roleName) {
      // keep name-only only if we can map to org role
      return;
    }
    if (!roleId) return;
    const orgMatch = (allRoles || []).find(
      (r) => String(r.roleId).toLowerCase() === roleId.toLowerCase(),
    );
    roleMap.set(roleId.toLowerCase(), {
      roleId: orgMatch?.roleId || roleId,
      roleName: orgMatch?.roleName || roleName || roleId,
    });
  };

  const staffById = new Map();
  for (const staff of staffList || []) {
    const sid = normId(staff?.StaffId);
    if (sid) staffById.set(sid, staff);
  }

  for (const sid of assignedIds) {
    const staff = staffById.get(sid);
    if (staff) {
      if (!staffHasSiteAccess(staff, siteId)) continue;
      addRole(
        staff.RoleId || staff.RoleIdRef || staff.roleId,
        staff.RoleName || staff.Roles || staff.roleName,
      );
      continue;
    }
    // Study user row hint when staff lookup row missing — only if site unknown; skip without staff site proof
    const hint = roleHintByStaff.get(sid);
    if (hint) {
      // Without staff site mapping we cannot prove site access; skip
    }
  }

  return finish([...roleMap.values()]);
}

export async function createReceiveScanSession(studyId, siteId) {
  const result = await postEpsf(API_ENDPOINTS.EPSF.CREATE_RECEIVE_SCAN_SESSION, {
    StudyId: studyId,
    SiteId: siteId,
  });
  const row = result.rows[0] || {};
  const code = String(
    row.ShortCode || row.shortCode || row.AccessToken || row.accessToken || "",
  )
    .trim()
    .toUpperCase();
  const ds = result.ds || {};
  const lanRows =
    (Array.isArray(ds.Txns) && ds.Txns) ||
    (Array.isArray(ds.txns) && ds.txns) ||
    [];
  const lanIps = (lanRows || [])
    .map((r) => ({
      ip: String(r.IpAddress || r.ipAddress || "").trim(),
      interfaceType: String(r.InterfaceType || r.interfaceType || ""),
      isPreferred: Number(r.IsPreferred ?? r.isPreferred ?? 0) === 1,
    }))
    .filter((r) => r.ip);
  return {
    ...result,
    session: {
      scanSessionId: String(row.ScanSessionId || row.scanSessionId || ""),
      accessToken: code,
      shortCode: code,
      expiresAt: row.ExpiresAt || row.expiresAt || null,
    },
    lanIps,
  };
}

export async function pollReceiveScanEvents(scanSessionId) {
  const result = await postEpsf(API_ENDPOINTS.EPSF.POLL_RECEIVE_SCAN_EVENTS, {
    ScanSessionId: scanSessionId,
  });
  const ds = result.ds || {};
  const sessionRow =
    (Array.isArray(ds.Data) && ds.Data[0]) ||
    (Array.isArray(ds.data) && ds.data[0]) ||
    result.rows[0] ||
    {};
  const events =
    (Array.isArray(ds.Txns) && ds.Txns) ||
    (Array.isArray(ds.txns) && ds.txns) ||
    [];
  return {
    ...result,
    session: {
      scanSessionId: String(sessionRow.ScanSessionId || sessionRow.scanSessionId || ""),
      status: String(sessionRow.Status || sessionRow.status || ""),
      expiresAt: sessionRow.ExpiresAt || sessionRow.expiresAt || null,
      isActive: Number(sessionRow.IsActive ?? sessionRow.isActive ?? 0) === 1,
    },
    events: (events || []).map((e) => ({
      scanEventId: String(e.ScanEventId || e.scanEventId || ""),
      kitNumber: String(e.KitNumber || e.kitNumber || "").trim(),
      batchNumber: String(e.BatchNumber || e.batchNumber || "").trim() || null,
      expiryDate: e.ExpiryDate || e.expiryDate || null,
      rawScanValue: e.RawScanValue || e.rawScanValue || null,
    })),
  };
}

export async function closeReceiveScanSession(scanSessionId) {
  return postEpsf(API_ENDPOINTS.EPSF.CLOSE_RECEIVE_SCAN_SESSION, {
    ScanSessionId: scanSessionId,
  });
}

/** Unauthenticated phone companion — no CTMS login. */
function resolvePublicApiBase(overrideBase) {
  const raw = String(overrideBase || "").trim();
  if (raw) return raw.replace(/\/?$/, "/");

  // Phone on LAN React URL (http://192.168.x.x:3000/s/CODE): use same origin so
  // CRA setupProxy.js can forward /api → localhost:50623 (IIS Express is
  // localhost-only and returns HTTP 400 on LAN IP).
  try {
    if (typeof window !== "undefined") {
      const host = String(window.location.hostname || "");
      const isLan =
        host &&
        host !== "localhost" &&
        host !== "127.0.0.1" &&
        /^\d+\.\d+\.\d+\.\d+$/.test(host);
      if (isLan) {
        return `${window.location.origin.replace(/\/$/, "")}/`;
      }
    }
  } catch {
    /* fall through */
  }

  try {
    // eslint-disable-next-line global-require
    const { API_CONFIG } = require("../../../config/apiconfig");
    return String(API_CONFIG.baseURL || "").replace(/\/?$/, "/");
  } catch {
    return "/";
  }
}

async function postEpsfPublic(path, body, apiBaseOverride = null) {
  const base = resolvePublicApiBase(apiBaseOverride);
  const url = path.startsWith("http")
    ? path
    : `${base}${String(path).replace(/^\//, "")}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  const success = Number(json?.success ?? json?.Success ?? 0) === 1;
  const message =
    json?.message || json?.Message || (!res.ok ? `HTTP ${res.status}` : "");
  const ds = getDataset(json);
  return { success, message, ds, rows: firstTable(ds), response: json };
}

export async function guestGetReceiveScanSession(accessToken, apiBaseOverride = null) {
  const result = await postEpsfPublic(
    API_ENDPOINTS.EPSF.PUBLIC_GET_RECEIVE_SCAN_SESSION,
    { AccessToken: accessToken },
    apiBaseOverride,
  );
  const row = result.rows[0] || {};
  return {
    ...result,
    guidance: row.Guidance || row.guidance || "",
    expiresAt: row.ExpiresAt || row.expiresAt || null,
  };
}

export async function guestPostReceiveScan({
  accessToken,
  kitNumber,
  batchNumber = null,
  expiryDate = null,
  rawScanValue = null,
  apiBaseOverride = null,
}) {
  return postEpsfPublic(
    API_ENDPOINTS.EPSF.PUBLIC_POST_RECEIVE_SCAN,
    {
      AccessToken: accessToken,
      KitNumber: kitNumber,
      BatchNumber: batchNumber,
      ExpiryDate: expiryDate,
      RawScanValue: rawScanValue,
    },
    apiBaseOverride,
  );
}

/** Phone ends session (refresh / leave). Safe to call repeatedly. */
export async function guestCloseReceiveScanSession(
  accessToken,
  apiBaseOverride = null,
) {
  return postEpsfPublic(
    API_ENDPOINTS.EPSF.PUBLIC_CLOSE_RECEIVE_SCAN_SESSION,
    { AccessToken: accessToken },
    apiBaseOverride,
  );
}

/** Best-effort close when the tab is closing / refreshing (keepalive). */
export function guestCloseReceiveScanSessionBeacon(
  accessToken,
  apiBaseOverride = null,
) {
  const token = String(accessToken || "").trim();
  if (!token || typeof window === "undefined") return;
  try {
    const base = resolvePublicApiBase(apiBaseOverride);
    const path = API_ENDPOINTS.EPSF.PUBLIC_CLOSE_RECEIVE_SCAN_SESSION;
    const url = path.startsWith("http")
      ? path
      : `${base}${String(path).replace(/^\//, "")}`;
    const body = JSON.stringify({ AccessToken: token });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

/* ===================== ePSF Binder (evidence repository) ===================== */

export async function fetchBinderSections() {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_BINDER_SECTIONS, {});
}

export async function fetchBinderDocuments(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_BINDER_DOCUMENTS, payload || {});
}

export async function fetchBinderDocument(binderDocumentId, studyId, siteId) {
  return postEpsf(API_ENDPOINTS.EPSF.GET_BINDER_DOCUMENT, {
    BinderDocumentId: binderDocumentId,
    StudyId: studyId || null,
    SiteId: siteId || null,
  });
}

export async function archiveBinderDocument(binderDocumentId, studyId, siteId) {
  return postEpsf(API_ENDPOINTS.EPSF.ARCHIVE_BINDER_DOCUMENT, {
    BinderDocumentId: binderDocumentId,
    StudyId: studyId || null,
    SiteId: siteId || null,
  });
}

export async function saveBinderTriggerFiling(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SAVE_BINDER_TRIGGER_FILING, payload || {});
}

export async function uploadBinderDocument(formData) {
  const response = await apiClient.post(
    API_ENDPOINTS.EPSF.UPLOAD_BINDER_DOCUMENT,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  const success = Number(response?.success ?? response?.Success ?? 0) === 1;
  const message = response?.message || response?.Message || "";
  const ds = getDataset(response);
  return { success, message, ds, rows: firstTable(ds), response };
}

export async function fetchBinderUploadRequests(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.LIST_BINDER_UPLOAD_REQUESTS, payload || {});
}

export async function saveBinderCustomSection(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SAVE_BINDER_CUSTOM_SECTION, payload || {});
}

export async function moveBinderSection(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.MOVE_BINDER_SECTION, payload || {});
}

export async function moveBinderDocument(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.MOVE_BINDER_DOCUMENT, payload || {});
}

export async function saveBinderUploadRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.SAVE_BINDER_UPLOAD_REQUEST, payload || {});
}

export async function cancelBinderUploadRequest(payload) {
  return postEpsf(API_ENDPOINTS.EPSF.CANCEL_BINDER_UPLOAD_REQUEST, payload || {});
}

